import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { validateEmail, validatePassword } from '@/utils/validators'
import { useForceLightMode } from '@/contexts/ThemeContext'

type RegisterForm = {
  name: string
  email: string
  phone: string
  password: string
  confirmPassword: string
  acceptTerms: boolean
}

type FormErrors = {
  name?: string
  email?: string
  phone?: string
  password?: string
  confirmPassword?: string
  acceptTerms?: string
}

// Accepts either 0-prefixed (0812...) or +62-prefixed Indonesian mobile numbers.
const PHONE_REGEX = /^(\+62|62|0|)8\d{8,12}$/

const RegisterPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, isLoading, isAuthenticated, error, register, clearError } = useAuth()
  useForceLightMode()

  const [formData, setFormData] = useState<RegisterForm>({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/courses')
    }
  }, [isAuthenticated, user, navigate])

  // Clear error on unmount
  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error for the field being edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name) {
      newErrors.name = 'Name is required'
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else {
      const emailValidation = validateEmail(formData.email)
      if (!emailValidation.isValid) {
        newErrors.email = emailValidation.error
      }
    }

    if (!formData.phone) {
      newErrors.phone = 'Nomor HP wajib diisi'
    } else {
      const sanitized = formData.phone.replace(/[\s-]/g, '')
      if (!PHONE_REGEX.test(sanitized)) {
        newErrors.phone = 'Format: 0812xxx atau +62812xxx'
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else {
      const passwordValidation = validatePassword(formData.password)
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors[0]
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phone: formData.phone,
      })
      // After successful registration, route user to verify-email flow.
      navigate('/verify-email')
    } catch (error) {
      // Error is handled by the useAuth hook
      console.error('Registration failed:', error)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center pt-32 md:pt-40 bg-slate-50 dark:bg-slate-950 py-12 px-4 relative overflow-hidden" data-testid="register-page">
      {/* ── Minimalist Background ── */}
      <div className="absolute top-[-10%] right-[-5%] w-[30%] h-[30%] bg-primary/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-[-5%] left-[-5%] w-[25%] h-[25%] bg-slate-200 dark:bg-primary/5 rounded-full blur-[80px]" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* ── Header ── */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            Daftar Akun Baru
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium">
            Bergabunglah dengan komunitas Pelatihan Merdeka
          </p>
        </div>

        {/* ── Register Card ── */}
        <div className="bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl p-8 sm:p-10 rounded-3xl border border-white dark:border-white/5 shadow-2xl shadow-slate-200/50 dark:shadow-none">
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* General error from auth context */}
            {error && (
              <div role="alert" className="bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 p-4 rounded-xl text-sm font-medium flex items-center gap-2">
                <span className="material-symbols-outlined text-[20px]">error</span>
                {error}
              </div>
            )}

            {/* Name field */}
            <div className="space-y-1.5">
              <label htmlFor="name" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                Nama Lengkap
              </label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[20px]">person</span>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="block w-full pl-12 pr-4 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-white/10 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                  placeholder="Nama lengkap Anda"
                  autoComplete="name"
                  required
                />
              </div>
              {errors.name && <p className="mt-1 text-xs font-medium text-rose-500 ml-1">{errors.name}</p>}
            </div>

            {/* Email field */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                Alamat Email
              </label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[20px]">mail</span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full pl-12 pr-4 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-white/10 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                  placeholder="nama@email.com"
                  autoComplete="email"
                  required
                />
              </div>
              {errors.email && <p className="mt-1 text-xs font-medium text-rose-500 ml-1">{errors.email}</p>}
            </div>

            {/* Phone field */}
            <div className="space-y-1.5">
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                Nomor HP
              </label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[20px]">call</span>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="block w-full pl-12 pr-4 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-white/10 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                  placeholder="0812xxxxxx"
                  autoComplete="tel"
                  required
                />
              </div>
              {errors.phone && <p className="mt-1 text-xs font-medium text-rose-500 ml-1">{errors.phone}</p>}
            </div>

            {/* Passwords */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                Kata Sandi
              </label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[20px]">lock</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full pl-12 pr-12 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-white/10 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                  placeholder="Buat kata sandi"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <span className="material-symbols-outlined text-[20px]">{showPassword ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
              {errors.password
                ? <p className="mt-1 text-xs font-medium text-rose-500 ml-1">{errors.password}</p>
                : <p className="mt-1 text-xs text-slate-400 ml-1">Min. 8 karakter, huruf besar, huruf kecil, dan angka</p>
              }
            </div>

            {/* Confirm Password field */}
            <div className="space-y-1.5">
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                Konfirmasi Kata Sandi
              </label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[20px]">lock_reset</span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="block w-full pl-12 pr-12 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-white/10 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                  placeholder="Ulangi kata sandi"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <span className="material-symbols-outlined text-[20px]">{showConfirmPassword ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs font-medium text-rose-500 ml-1">{errors.confirmPassword}</p>}
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-white/5 rounded-2xl">
              <input
                type="checkbox"
                id="acceptTerms"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleInputChange}
                className="size-4 mt-0.5 accent-primary rounded-lg"
                required
              />
              <label htmlFor="acceptTerms" className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-snug">
                Saya menyetujui <Link to="/terms" className="text-primary font-bold">Syarat & Ketentuan</Link> serta <Link to="/privacy" className="text-primary font-bold">Kebijakan Privasi</Link>
              </label>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 px-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 active:scale-[0.98] transition-all shadow-lg shadow-slate-200 dark:shadow-none disabled:opacity-50 mt-2"
            >
              {isLoading ? 'Mendaftar...' : 'Buat Akun'}
            </button>

            {/* Sign in link */}
            <div className="text-center pt-2">
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                Sudah punya akun?{' '}
                <Link to="/login" className="text-primary font-bold hover:underline">Masuk</Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
